import React, { useContext, useEffect, useMemo } from "react";
import PmoTooltip from "../../../ProductMarketingOptimization/components/common/PmoTooltip";
import CompetitorSelect from "../competitorSelect";
import { RouteComponentProps, withRouter } from "react-router-dom";

import NIMLatestChart from "../../../../components/Charts/BrandPerformance/NIMLatestChart";
import { ThemeContext } from "../../../../context";

import { useQuery } from "@tanstack/react-query";
import { RootStateOrAny, useSelector } from "react-redux";
import { ErrorBoundary } from "@sentry/react";
import { ErrorMsg, NoDataMsg } from "../../../../components/AppMessages";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { getNIMLatestChartData, getNIMLatestTable } from "../../../../api/brandPerformanceApi";

import "../../../../assets/styles/component/tables.scss";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  diff: number;
  nimLatestRanking: number;
  granularity: string;
}

interface ITableData {
  market: string;
  latest_score: number;
  latest_date: string;
  previous_score: number;
  previous_date: string;
  significant_change: 0 | 1 | -1;
}
const NIMLatestContent = withRouter((props: Props) => {
  const { diff, nissanCompetitor, onHandleRadioClick, history, nimLatestRanking, granularity } = props;

  const { theme } = useContext(ThemeContext);

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const { market, granularityParam } = params;

  // fetch the trend and average data
  const {
    data: chartData,
    isFetching: loadingChart, // call data using react-query
    refetch: refetchNIMLatestChartData,
  } = useQuery(
    ["NIMLatestChartData", granularity, history.location.search],
    () => getNIMLatestChartData({ ...params, nissan_competitor: nissanCompetitor }),
    {
      enabled: false,
    }
  );

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchNIMLatestTableData,
  } = useQuery(
    ["NIMLatestTableData", granularity, history.location.search],
    () => getNIMLatestTable({ ...params, nissan_competitor: nissanCompetitor }),
    {
      enabled: false,
    }
  );

  // when the url changes, refetch the data
  useEffect(() => {
    refetchNIMLatestChartData();
    refetchNIMLatestTableData();
  }, [history.location.search]);

  const countryKeys: string[] = tableData?.length > 0 ? tableData.map((item: ITableData) => item?.market) : [];

  const topModels = useMemo(() => {
    return chartData?.series?.slice(-10)?.map((series: { name: any }) => series?.name);
  }, [chartData]);

  // Transform chartData.data to extract score and rank from object fields for each series
  const transformedChartData = useMemo(() => {
    if (!chartData || !Array.isArray(chartData.data) || !Array.isArray(chartData.series)) return chartData;
    const seriesList: Array<{ field: string }> = chartData.series;
    const newData = chartData.data.map((row: Record<string, any>) => {
      const newRow: Record<string, any> = { ...row };
      seriesList.forEach(({ field }) => {
        const val = row[field];
        newRow[field] = val && typeof val === "object" && val.rank != null ? Number(val.rank) : null;
        // Add rank as a separate field if present
        newRow[`${field}_score`] = val && typeof val === "object" && val.score != null ? Number(val.score) : null;
      });
      return newRow;
    });
    return { ...chartData, data: newData };
  }, [chartData]);

  return (
    <div className="nim-latest primary_background ">
      <div className="header" style={{ paddingTop: "1em", paddingLeft: "1em" }}>
        <div className="title_container">
          <>
            <h3>NIM Latest</h3>
          </>
          {nissanCompetitor && <CompetitorSelect onHandleRadioClick={onHandleRadioClick} nissanCompetitor={nissanCompetitor} />}
        </div>
      </div>
      <div className="chart_content">
        {transformedChartData?.data?.length && !loadingChart ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <NIMLatestChart
              data={transformedChartData?.data ?? []}
              seriesList={transformedChartData.series?.map((val: any) => ({ ...val, hide: !topModels?.includes(val?.name) }))}
              chartId="brandSnapshotChart"
              theme={theme}
              granularity={granularity ? granularity : granularityParam}
              market={market}
              diff={diff}
              nimLatestRanking={nimLatestRanking}
              percentageChart={false}
            />
          </ErrorBoundary>
        ) : loadingChart ? (
          <LoadingEllipsis isLoading={loadingChart} />
        ) : (
          // eslint-disable-next-line react/jsx-no-undef
          <NoDataMsg message="No data available" />
        )}
      </div>

      {/* table */}

      <div className="nim-latest_table">
        {tableData?.length && !loadingTable ? (
          <table>
            <thead>
              <tr>
                <th>Score </th>
                {countryKeys.map((country) => (
                  <th key={country}>{country}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="odd">
                <td>Latest</td>
                {countryKeys.map((country) => {
                  const latest_score = tableData?.find((item: ITableData) => item.market === country);
                  return (
                    <td key={country}>
                      {" "}
                      {latest_score?.latest_score ? `${latest_score.latest_score}% (${latest_score.latest_date})` : "N/A"}
                    </td>
                  );
                })}
              </tr>
              <tr className="even">
                <td>Previous</td>
                {countryKeys.map((country) => {
                  const prev_score = tableData?.find((item: ITableData) => item.market === country);

                  return (
                    <td key={country}>
                      {prev_score?.previous_score ? `${prev_score?.previous_score}% ( ${prev_score?.previous_date})` : "N/A"}
                    </td>
                  );
                })}
              </tr>
              <tr className="odd">
                <td className="significant-change-td">
                  {" "}
                  <span className={`indicator up`} /> <span className={`indicator down-red`} />
                </td>
                {countryKeys.map((country) => {
                  const indicator = tableData?.find((item: ITableData) => item.market === country)?.significant_change;
                  return (
                    <td key={country} className="performance_indicator">
                      {indicator == 1 ? (
                        <span className={`indicator up`} />
                      ) : indicator == -1 ? (
                        <span className={`indicator down-red`} />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        ) : loadingTable ? (
          <LoadingEllipsis isLoading={loadingTable} />
        ) : (
          <NoDataMsg message="No score data available" />
        )}
      </div>
    </div>
  );
});

export default NIMLatestContent;
